/**
 * Zod schema for the Part-Time Rule create/edit form (Phase 26 / PAY2-02, be-207).
 *
 * Business rules (be-207 §Plan 26-03 PT-rules):
 *   - kind: "HOURLY" | "PROJECT_BASED" (discriminated union)
 *   - HOURLY: requires hourlyRate (positive number), forbids projectFixedAmount
 *   - PROJECT_BASED: requires projectFixedAmount (positive), optionally projectName (max 120)
 *   - effectiveFrom: required ISO date string
 *   - effectiveTo: optional nullable ISO date string
 *   - currencyCode: required 3-char ISO 4217 string (e.g. "JOD")
 *
 * XOR enforcement: 400 PART_TIME_RULE_KIND_FIELDS_MISMATCH from backend.
 * FE enforces upfront via discriminatedUnion so the right fields are required
 * per kind and excess fields on the wrong kind are rejected.
 *
 * Exported for both the form component and contract test suite.
 * TODO: derive from generated schema after CC-142 openapi regen (Plan 26-05).
 */
import { z } from "zod";

const commonFields = {
  effectiveFrom: z.string().min(1, "Effective from date is required"),
  effectiveTo: z.string().nullable().optional(),
  currencyCode: z.string().length(3, "Currency code must be 3 characters (e.g. JOD)"),
};

export const partTimeRuleFormSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("HOURLY"),
    hourlyRate: z
      .number({ invalid_type_error: "Hourly rate must be a number" })
      .positive("Hourly rate must be positive"),
    ...commonFields,
  }),
  z.object({
    kind: z.literal("PROJECT_BASED"),
    projectFixedAmount: z
      .number({ invalid_type_error: "Project amount must be a number" })
      .positive("Project amount must be positive"),
    projectName: z.string().max(120, "Project name must be 120 characters or fewer").optional(),
    ...commonFields,
  }),
]);

export type PartTimeRuleFormValues = z.infer<typeof partTimeRuleFormSchema>;
